// Pure calculations. Each returns the value plus a human-readable trace of how it was computed.
import { D, type Dec, ZERO, max0, minD, cents, fmt } from "./money.ts";
import { RULES_2026 as R, type FilingStatus } from "./rules/2026.ts";

export interface TraceStep {
  label: string;
  value: string;
  rule?: string;
}

export interface Computed<T> {
  value: T;
  trace: TraceStep[];
}

// ---------------------------------------------------------------- Section 1256

export interface Form1099BFutures {
  /** Box 8: realized profit/(loss) on closed contracts. Null when the box is absent. */
  box8: Dec | null;
  /** Box 9: unrealized profit/(loss) on open contracts at prior year end. */
  box9: Dec | null;
  /** Box 10: unrealized profit/(loss) on open contracts at current year end. */
  box10: Dec | null;
  /** Box 11: aggregate profit/(loss). The broker's figure is the starting point for Form 6781 line 1. */
  box11: Dec;
}

export type BoxCheck =
  | { state: "agrees"; computed: Dec }
  | { state: "mismatch"; computed: Dec; difference: Dec }
  | { state: "unavailable"; missing: string[] };

/** Check Box 11 against Box 8 − Box 9 + Box 10. Never "fixes" the broker's figure. */
export function checkBox11(f: Form1099BFutures): BoxCheck {
  const missing = (["box8", "box9", "box10"] as const).filter((k) => f[k] === null);
  if (missing.length) return { state: "unavailable", missing };
  const computed = f.box8!.minus(f.box9!).plus(f.box10!);
  const difference = f.box11.minus(computed);
  return difference.isZero() ? { state: "agrees", computed } : { state: "mismatch", computed, difference };
}

/** Form 6781 lines 7–9 for the ordinary case (no straddles, hedges, carryback or §475). */
export function split1256(netGainOrLoss: Dec): Computed<{ shortTerm: Dec; longTerm: Dec }> {
  const shortTerm = netGainOrLoss.times(R.section1256.shortTermShare);
  const longTerm = netGainOrLoss.minus(shortTerm);
  return {
    value: { shortTerm, longTerm },
    trace: [
      { label: "Net Section 1256 gain or (loss) (Form 6781 line 7)", value: fmt(netGainOrLoss) },
      { label: "Short-term 40% (line 8 → Schedule D line 4)", value: fmt(shortTerm), rule: R.sources.section1256 },
      { label: "Long-term 60% (line 9 → Schedule D line 11)", value: fmt(longTerm), rule: R.sources.section1256 },
    ],
  };
}

/** Sum line 1 entries across brokers (Form 6781 lines 2–3). */
export function net1256(entries: Dec[]): Dec {
  return entries.reduce((a, b) => a.plus(b), ZERO);
}

// ---------------------------------------------------------------- Self-employment tax

export interface SeInput {
  /** Combined net profit from all of this person's Schedule C businesses (can be negative). */
  netProfit: Dec;
  /** This person's W-2 Social Security wages (box 3 + box 7) for the year. */
  socialSecurityWages: Dec;
}

export interface SeResult {
  /** Profit × 92.35%, shown even when it falls under the $400 minimum. */
  netEarnings: Dec;
  /** Net earnings that are actually subject to SE tax: zero under the $400 minimum (Schedule SE line 6 → Form 8959 line 8). */
  taxableSeIncome: Dec;
  socialSecurityTax: Dec;
  medicareTax: Dec;
  seTax: Dec;
  deductibleHalf: Dec;
}

/** Regular-method SE tax for one person (Schedule SE Part I). Spouses are computed separately. */
export function seTax(input: SeInput): Computed<SeResult> {
  const r = R.seTax;
  const netEarnings = max0(input.netProfit).times(r.netEarningsFactor);
  const trace: TraceStep[] = [
    { label: "Net profit from Schedule C", value: fmt(input.netProfit) },
    { label: "× 92.35% = net earnings from self-employment", value: fmt(netEarnings), rule: R.sources.seTax },
  ];
  if (netEarnings.lessThan(r.minimumNetEarnings)) {
    trace.push({ label: "Under $400, so no SE tax is due", value: "0.00", rule: R.sources.seTax });
    return { value: { netEarnings, taxableSeIncome: ZERO, socialSecurityTax: ZERO, medicareTax: ZERO, seTax: ZERO, deductibleHalf: ZERO }, trace };
  }
  const roomUnderBase = max0(new D(r.socialSecurityWageBase).minus(input.socialSecurityWages));
  const ssBase = minD(netEarnings, roomUnderBase);
  const socialSecurityTax = ssBase.times(r.socialSecurityRate);
  const medicareTax = netEarnings.times(r.medicareRate);
  const total = socialSecurityTax.plus(medicareTax);
  const half = total.div(2);
  trace.push(
    { label: "Room left under the $184,500 Social Security wage base", value: fmt(roomUnderBase), rule: R.sources.ssWageBase },
    { label: "Social Security part (12.4%)", value: fmt(socialSecurityTax) },
    { label: "Medicare part (2.9%, no cap)", value: fmt(medicareTax) },
    { label: "Self-employment tax", value: fmt(total) },
    { label: "Deductible half (Schedule 1)", value: fmt(half) },
  );
  return {
    value: { netEarnings, taxableSeIncome: netEarnings, socialSecurityTax, medicareTax, seTax: total, deductibleHalf: half },
    trace,
  };
}

// ---------------------------------------------------------------- Additional Medicare Tax (Form 8959)

/**
 * Household Additional Medicare Tax. `medicareWages` is W-2 box 5 for everyone on the return;
 * `taxableSeIncome` is SE income that passed the $400 minimum (an SE loss counts as zero).
 */
export function additionalMedicareTax(status: FilingStatus, medicareWages: Dec, taxableSeIncome: Dec): Computed<Dec> {
  const threshold = new D(R.additionalMedicare.threshold[status]);
  const onWages = max0(medicareWages.minus(threshold));
  const reducedThreshold = max0(threshold.minus(medicareWages));
  const onSe = max0(max0(taxableSeIncome).minus(reducedThreshold));
  const tax = onWages.plus(onSe).times(R.additionalMedicare.rate);
  return {
    value: tax,
    trace: [
      { label: "Threshold for filing status", value: fmt(threshold), rule: R.sources.additionalMedicare },
      { label: "Wages over threshold", value: fmt(onWages) },
      { label: "Threshold left for self-employment income", value: fmt(reducedThreshold) },
      { label: "Self-employment earnings over remaining threshold", value: fmt(onSe) },
      { label: "Additional Medicare Tax (0.9%)", value: fmt(tax) },
    ],
  };
}

// ---------------------------------------------------------------- NIIT (Form 8960)

export function niit(status: FilingStatus, netInvestmentIncome: Dec, magi: Dec): Computed<Dec> {
  const threshold = new D(R.niit.threshold[status]);
  const excess = max0(magi.minus(threshold));
  const base = minD(max0(netInvestmentIncome), excess);
  const tax = base.times(R.niit.rate);
  return {
    value: tax,
    trace: [
      { label: "Modified AGI over threshold", value: fmt(excess), rule: R.sources.niit },
      { label: "Smaller of that or net investment income", value: fmt(base) },
      { label: "Net investment income tax (3.8%)", value: fmt(tax) },
    ],
  };
}

// ---------------------------------------------------------------- Regular income tax (rate schedules)

/** Tax on ordinary taxable income using the rate schedule (not the $50 Tax Table bands). */
export function ordinaryTax(status: FilingStatus, taxableIncome: Dec): Dec {
  const brackets = R.brackets[status];
  let tax = ZERO;
  const ti = max0(taxableIncome);
  for (const [i, bracket] of brackets.entries()) {
    const lo = new D(bracket.from);
    const next = brackets[i + 1];
    const hi = next ? new D(next.from) : null;
    if (ti.lessThanOrEqualTo(lo)) break;
    const top = hi && ti.greaterThan(hi) ? hi : ti;
    tax = tax.plus(top.minus(lo).times(bracket.rate));
  }
  return tax;
}

/**
 * Qualified Dividends and Capital Gain Tax Worksheet logic.
 * preferentialIncome = net long-term gain (after netting with short-term) + qualified dividends, limited to taxable income.
 */
export function incomeTax(status: FilingStatus, taxableIncome: Dec, preferentialIncome: Dec): Computed<Dec> {
  const ti = max0(taxableIncome);
  const pref = minD(max0(preferentialIncome), ti);
  const ordinary = ti.minus(pref);
  const cg = R.capitalGains[status];
  const zeroTop = new D(cg.zeroTop);
  const fifteenTop = new D(cg.fifteenTop);

  const atZero = max0(minD(ti, zeroTop).minus(ordinary));
  const atFifteen = max0(minD(ti, fifteenTop).minus(ordinary.plus(atZero)));
  const atTwenty = pref.minus(atZero).minus(atFifteen);

  const ordTax = ordinaryTax(status, ordinary);
  const prefTax = atFifteen.times("0.15").plus(atTwenty.times("0.20"));
  const withPref = ordTax.plus(prefTax);
  const allOrdinary = ordinaryTax(status, ti);
  const tax = minD(withPref, allOrdinary);
  return {
    value: tax,
    trace: [
      { label: "Taxable income", value: fmt(ti) },
      { label: "Long-term gains and qualified dividends taxed at lower rates", value: fmt(pref) },
      { label: "Tax on ordinary portion (rate schedule)", value: fmt(ordTax), rule: R.sources.brackets },
      { label: "Taxed at 0%", value: fmt(atZero), rule: R.sources.capitalGains },
      { label: "Taxed at 15%", value: fmt(atFifteen) },
      { label: "Taxed at 20%", value: fmt(atTwenty) },
      { label: "Income tax (smaller of worksheet or all-ordinary tax)", value: fmt(tax) },
    ],
  };
}

// ---------------------------------------------------------------- Estimated tax safe harbor (Form 1040-ES)

export interface EstimatedPayment {
  amount: Dec;
  /**
   * The due date this payment was made on or before (one of the year's due dates). A payment made after
   * one due date counts toward the next. Timing matters: a late payment doesn't make an earlier
   * installment on time (Form 2210).
   */
  paidBy: string;
}

export interface SafeHarborInput {
  status: FilingStatus;
  /** Prior-year tax per the 1040-ES worksheet (2025 Form 1040 line 24 less the specified items). Null if not provided. */
  priorYearTax: Dec | null;
  /** Prior-year AGI (2025 Form 1040 line 11). Required when priorYearTax is given. */
  priorYearAgi: Dec | null;
  /** True if the prior year was a full 12-month tax year with a return filed. */
  priorYearFullYearReturn: boolean;
  /** Expected current-year total tax, if the user has a complete estimate. Null when unknown. */
  expectedCurrentYearTax: Dec | null;
  /** Expected withholding for the year. Withholding counts as paid evenly through the year. */
  expectedWithholding: Dec;
  /** Estimated payments already made for this year, each with the due date it met. */
  payments: EstimatedPayment[];
}

type Basis = "90% of current year" | "100% of prior year" | "110% of prior year";

export type SafeHarborResult =
  | { state: "needs_input"; required: string[] }
  | { state: "invalid"; errors: string[] }
  | {
      state: "computed";
      requiredAnnualPayment: Dec;
      basis: Basis;
      paidSoFar: Dec;
      remainingAfterWithholdingAndPayments: Dec;
      /**
       * Under equal installments, the part of the target that was due on dates already passed and hasn't
       * been paid. Pay it now: the penalty grows each day it stays unpaid.
       */
      pastDue: Dec;
      /** What's left after the past-due amount, spread over the remaining due dates (cents balance on the last). */
      upcoming: { dueDate: string; amount: Dec }[];
      /** Passed due dates where payments plus withholding were behind the target at the time. */
      shortDueDates: string[];
      /** True once the year's last due date has passed. */
      afterFinalDueDate: boolean;
      noPaymentsNeeded: boolean;
    };

/**
 * Required annual payment to avoid the underpayment penalty (regular installment method, equal installments).
 * `asOf` is passed in (no clock access) to decide which due dates remain. A shortfall is only "possible":
 * the annualized income method (Schedule AI) can lower earlier installments when income came late in the year.
 */
export function safeHarbor(input: SafeHarborInput, asOf: string): Computed<SafeHarborResult> {
  const es = R.estimatedTax;
  const trace: TraceStep[] = [];
  const required: string[] = [];
  const errors: string[] = [];
  const nonNegative: [string, Dec | null][] = [
    ["priorYearTax", input.priorYearTax],
    ["expectedCurrentYearTax", input.expectedCurrentYearTax],
    ["expectedWithholding", input.expectedWithholding],
    ...input.payments.map((p, i): [string, Dec] => [`payments[${i}]`, p.amount]),
  ];
  for (const [name, v] of nonNegative) if (v !== null && v.lessThan(0)) errors.push(name);
  for (const [i, p] of input.payments.entries()) if (!es.dueDates.includes(p.paidBy as never)) errors.push(`payments[${i}].paidBy`);
  if (errors.length) return { value: { state: "invalid", errors }, trace };

  const priorUsable = input.priorYearFullYearReturn && input.priorYearTax !== null;
  if (input.priorYearTax !== null && input.priorYearAgi === null) required.push("priorYearAgi");
  if (!priorUsable && input.expectedCurrentYearTax === null) required.push("priorYearTax or expectedCurrentYearTax");
  if (required.length) return { value: { state: "needs_input", required }, trace };

  const candidates: { amount: Dec; basis: Basis }[] = [];
  if (input.expectedCurrentYearTax !== null) {
    const c = input.expectedCurrentYearTax.times(es.currentYearShare);
    candidates.push({ amount: c, basis: "90% of current year" });
    trace.push({ label: "90% of expected 2026 tax", value: fmt(c), rule: R.sources.estimatedTax });
  }
  if (priorUsable) {
    const limit = new D(input.status === "mfs" ? es.highIncomeAgiMfs : es.highIncomeAgi);
    const high = input.priorYearAgi!.greaterThan(limit);
    const share = high ? es.priorYearShareHighIncome : es.priorYearShare;
    const p = input.priorYearTax!.times(share);
    candidates.push({ amount: p, basis: high ? "110% of prior year" : "100% of prior year" });
    trace.push({
      label: high ? "110% of 2025 tax (2025 AGI over the limit)" : "100% of 2025 tax",
      value: fmt(p),
      rule: R.sources.estimatedTax,
    });
  }
  const pick = candidates.reduce((a, b) => (b.amount.lessThan(a.amount) ? b : a));
  const target = pick.amount;
  trace.push({ label: `Required annual payment (smaller amount: ${pick.basis})`, value: fmt(target) });

  const n = es.dueDates.length;
  const withholding = input.expectedWithholding;
  const paidSoFar = input.payments.reduce((a, p) => a.plus(p.amount), ZERO);
  const remaining = max0(target.minus(withholding).minus(paidSoFar));
  trace.push(
    { label: "Less expected withholding", value: fmt(withholding) },
    { label: "Less estimated payments already made", value: fmt(paidSoFar) },
    { label: "Still to pay", value: fmt(remaining) },
  );

  // Each passed due date: was the running total (withholding counted evenly, plus payments made by then)
  // at least the running share of the target?
  const passed = es.dueDates.filter((d) => d < asOf);
  const owesUnderMinimum =
    input.expectedCurrentYearTax !== null &&
    input.expectedCurrentYearTax.minus(withholding).lessThan(es.minimumBalanceDue);
  const shortDueDates: string[] = [];
  let lastShortfall = ZERO;
  for (const [i, due] of passed.entries()) {
    if (owesUnderMinimum) break;
    const share = new D(i + 1).div(n);
    const paidByThen = input.payments.filter((p) => p.paidBy <= due).reduce((a, p) => a.plus(p.amount), ZERO);
    const behind = target.times(share).minus(withholding.times(share)).minus(paidByThen);
    if (behind.greaterThan(0)) {
      shortDueDates.push(due);
      lastShortfall = behind;
    }
  }
  const passedShare = new D(passed.length).div(n);
  const pastDue = owesUnderMinimum ? ZERO : max0(target.minus(withholding).times(passedShare).minus(paidSoFar));
  if (pastDue.greaterThan(0)) {
    trace.push({ label: `Due by ${passed[passed.length - 1]} and not yet paid (pay now)`, value: fmt(pastDue), rule: R.sources.estimatedTax });
  }

  const remainingDates = es.dueDates.filter((d) => d >= asOf);
  const future = owesUnderMinimum ? ZERO : max0(remaining.minus(pastDue));
  const per = remainingDates.length ? cents(future.div(remainingDates.length)) : ZERO;
  const upcoming = remainingDates.map((dueDate, i) => ({
    dueDate,
    amount: i === remainingDates.length - 1 ? future.minus(per.times(remainingDates.length - 1)) : per,
  }));
  for (const u of upcoming) trace.push({ label: `Due ${u.dueDate}`, value: fmt(u.amount) });
  if (shortDueDates.length) {
    trace.push({
      label: `Behind the target on ${shortDueDates.join(", ")} (amount behind on the last of those). A penalty may apply for those periods unless the annualized income method (Form 2210 Schedule AI) lowers them`,
      value: fmt(lastShortfall),
      rule: "Form 2210 instructions",
    });
  }

  if (owesUnderMinimum) {
    trace.push({ label: "Expected 2026 tax after withholding is under $1,000, so no estimated payments are required", value: fmt(input.expectedCurrentYearTax!.minus(withholding)), rule: R.sources.estimatedTax });
  }

  return {
    value: {
      state: "computed",
      requiredAnnualPayment: target,
      basis: pick.basis,
      paidSoFar,
      remainingAfterWithholdingAndPayments: remaining,
      pastDue,
      upcoming,
      shortDueDates,
      afterFinalDueDate: remainingDates.length === 0,
      noPaymentsNeeded: remaining.isZero() || owesUnderMinimum,
    },
    trace,
  };
}
